import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { ChipList } from "@/components/ChipList";
import { useConfirm } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { KebabMenu } from "@/components/KebabMenu";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { StatusPill } from "@/components/StatusPill";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { hasRole } from "@/lib/hasRole";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { CreateTokenModal } from "./tokens/CreateTokenModal";
import {
  canCreateToken,
  permissionLabel,
  type Token,
  TOKEN_TYPES,
  type TokenState,
  tokenState,
  tokenTypeLabel,
} from "./tokens/shared";

interface TeamOption {
  id: string;
  name: string;
}

const COLUMNS = [
  "Token",
  "Scope",
  "Permissions",
  "Last used",
  "Expires",
  "Status",
] as const;

const STATE_LABEL: Record<TokenState, string> = {
  active: "Active",
  expired: "Expired",
  revoked: "Revoked",
};

const STATE_VARIANT: Record<TokenState, "active" | "idle" | "suspended"> = {
  active: "active",
  expired: "idle",
  revoked: "suspended",
};

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="flex flex-col gap-1.5">
              <div className="h-3 w-36 rounded bg-surface" />
              <div className="h-2.5 w-28 rounded bg-surface" />
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-24 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-5 w-40 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-5 w-20 rounded-full bg-surface" />
          </td>
          <td />
        </tr>
      ))}
    </>
  );
}

export function Tokens() {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [tokens, setTokens] = useState<Token[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [scopeFilter, setScopeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [teams, setTeams] = useState<TeamOption[] | null>(null);
  const [teamsFailed, setTeamsFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actioningTokenId, setActioningTokenId] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const isAdmin = hasRole(user?.roles, "Admin");
  const hasFilters = Boolean(
    debouncedNameFilter || scopeFilter || statusFilter,
  );

  const fetchTokens = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    for (const type of scopeFilter
      ? [scopeFilter]
      : [TOKEN_TYPES.team, TOKEN_TYPES.organization]) {
      params.append("types", type);
    }

    if (debouncedNameFilter) {
      params.set("name", debouncedNameFilter);
    }

    if (statusFilter) {
      params.set("status", statusFilter);
    }

    return api.get<PagedResult<Token>>(
      `/organizations/${orgId}/tokens?${params.toString()}`,
    );
  }, [
    accessToken,
    api,
    orgId,
    page,
    debouncedNameFilter,
    scopeFilter,
    statusFilter,
  ]);

  const applyTokens = useCallback((data: PagedResult<Token> | null) => {
    if (!data) {
      setError("Couldn't load tokens.");
      return;
    }

    setTokens(data.items);
    setTotalCount(data.totalCount);
    setError(null);
  }, []);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void fetchTokens().then((data) => {
      if (!cancelled) {
        applyTokens(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, fetchTokens, applyTokens]);

  const fetchTeams = useCallback(async (): Promise<TeamOption[] | null> => {
    if (!accessToken || !orgId) {
      return null;
    }

    const teamPageSize = 100;
    const scope = isAdmin ? "" : `&memberUserId=${user?.id}`;

    const first = await api.get<PagedResult<TeamOption>>(
      `/organizations/${orgId}/teams?page=1&pageSize=${teamPageSize}${scope}`,
    );

    if (!first) {
      return null;
    }

    const laterPages = Math.ceil(first.totalCount / teamPageSize) - 1;

    if (laterPages < 1) {
      return first.items;
    }

    const rest = await Promise.all(
      Array.from({ length: laterPages }, (_, index) =>
        api.get<PagedResult<TeamOption>>(
          `/organizations/${orgId}/teams?page=${index + 2}&pageSize=${teamPageSize}${scope}`,
        ),
      ),
    );

    if (rest.some((page) => page === null)) {
      return null;
    }

    return [...first.items, ...rest.flatMap((page) => page!.items)];
  }, [accessToken, api, orgId, isAdmin, user?.id]);

  const applyTeams = useCallback((items: TeamOption[] | null) => {
    if (!items) {
      setTeamsFailed(true);
      return;
    }

    setTeams(items);
    setTeamsFailed(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchTeams().then((items) => {
      if (!cancelled) {
        applyTeams(items);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTeams, applyTeams]);

  function clearFilters() {
    setNameFilter("");
    setScopeFilter("");
    setStatusFilter("");
    setPage(1);
  }

  function retry() {
    setError(null);
    setTokens(null);
    void fetchTokens().then(applyTokens);
  }

  async function revokeToken(token: Token) {
    const confirmed = await confirm(
      "Revoking this token will immediately prevent it from being used to authenticate.",
      { title: `Revoke ${token.name}?`, confirmLabel: "Revoke" },
    );

    if (!confirmed) {
      return;
    }

    setActioningTokenId(token.id);

    try {
      await api.send(
        `/organizations/${orgId}/tokens/${token.id}/revoke`,
        "POST",
        undefined,
        "Couldn't revoke token.",
      );

      showToast(`${token.name} revoked.`, "success");
      applyTokens(await fetchTokens());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't revoke token.",
        "error",
      );
    } finally {
      setActioningTokenId(null);
    }
  }

  async function deleteToken(token: Token) {
    const confirmed = await confirm(
      `"${token.name}" will be deleted and can't be recovered.`,
      { title: `Delete ${token.name}?`, confirmLabel: "Delete" },
    );

    if (!confirmed) {
      return;
    }

    setActioningTokenId(token.id);

    try {
      await api.send(
        `/organizations/${orgId}/tokens/${token.id}`,
        "DELETE",
        undefined,
        "Couldn't delete token.",
      );

      showToast(`${token.name} deleted.`, "success");
      applyTokens(await fetchTokens());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't delete token.",
        "error",
      );
    } finally {
      setActioningTokenId(null);
    }
  }

  const isLoading = tokens === null && !error;
  const isEmpty = tokens !== null && totalCount === 0;
  const canCreate = canCreateToken({ teams, teamsFailed, isAdmin });

  return (
    <div className="text-foreground">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tokens</h1>
          {tokens !== null && (
            <p className="mt-0.5 text-sm text-foreground/70">
              {plural(totalCount, "token")}
            </p>
          )}
        </div>
        <span
          title={
            !teamsFailed && teams !== null && !canCreate
              ? "You need to belong to a team before you can create a token."
              : undefined
          }
        >
          <Button
            variant="primary"
            disabled={!canCreate}
            onClick={() => setShowCreateModal(true)}
          >
            Create token
          </Button>
        </span>
      </div>

      <div className="mb-4 flex gap-2">
        <SearchField
          value={nameFilter}
          onChange={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
          label="Search tokens by name"
          placeholder="Search token name"
        />
        <Select
          aria-label="Filter by scope"
          value={scopeFilter}
          onChange={(e) => {
            setScopeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All scopes</option>
          <option value={TOKEN_TYPES.team}>
            {tokenTypeLabel(TOKEN_TYPES.team)}
          </option>
          <option value={TOKEN_TYPES.organization}>
            {tokenTypeLabel(TOKEN_TYPES.organization)}
          </option>
        </Select>
        <Select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="Active">{STATE_LABEL.active}</option>
          <option value="Expired">{STATE_LABEL.expired}</option>
          <option value="Revoked">{STATE_LABEL.revoked}</option>
        </Select>
      </div>

      {hasFilters && tokens !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(totalCount, "token")}</span>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {showCreateModal && teams !== null && (
        <CreateTokenModal
          teams={teams}
          canCreateOrganizationTokens={isAdmin}
          showToast={showToast}
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => applyTokens(await fetchTokens())}
        />
      )}

      {(error || teamsFailed) && (
        <LoadError
          what={error ? "tokens" : "teams"}
          onRetry={() => {
            if (error) {
              retry();
            }

            if (teamsFailed) {
              void fetchTeams().then(applyTeams);
            }
          }}
          className="mb-4"
        />
      )}

      {isEmpty ? (
        <EmptyState
          title={hasFilters ? "No tokens match these filters" : "No tokens yet"}
          description={
            hasFilters
              ? "Try a different name, scope, or status."
              : "Tokens provide programmatic access to the Tendra API."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
                <th scope="col" className="border-b border-surface pb-2.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                tokens?.map((token) => {
                  const state = tokenState(token);

                  return (
                    <tr
                      key={token.id}
                      className="h-14 border-b border-surface/70 hover:bg-surface"
                    >
                      <td className="py-2 pr-4">
                        <div className="min-w-0">
                          <div className="truncate text-foreground">
                            {token.name}
                          </div>
                          <div className="truncate text-xs text-foreground/70">
                            {token.createdBy
                              ? `Created by ${token.createdBy.name} · ${formatDate(token.createdAt)}`
                              : `Created ${formatDate(token.createdAt)}`}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-foreground/80">
                        <div>{token.team?.name ?? "Organization"}</div>
                        <div className="text-xs text-foreground/70">
                          {tokenTypeLabel(token.type)}
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        <ChipList
                          items={token.permissions.map(permissionLabel)}
                        />
                      </td>
                      <td className="py-2 pr-4 text-foreground/80">
                        {token.lastUsedAt
                          ? formatRelativeTime(token.lastUsedAt)
                          : "Never"}
                      </td>
                      <td className="py-2 pr-4 text-foreground/80">
                        {token.expiresAt ? formatDate(token.expiresAt) : "—"}
                      </td>
                      <td className="py-2 pr-4">
                        <StatusPill variant={STATE_VARIANT[state]}>
                          {STATE_LABEL[state]}
                        </StatusPill>
                      </td>
                      <td className="py-2 text-right">
                        <KebabMenu
                          label={`Actions for ${token.name}`}
                          items={[
                            {
                              label: "Revoke",
                              onClick: () => void revokeToken(token),
                              disabled:
                                state === "revoked" ||
                                actioningTokenId === token.id,
                            },
                            {
                              label: "Delete",
                              onClick: () => void deleteToken(token),
                              danger: true,
                              disabled:
                                state !== "revoked" ||
                                actioningTokenId === token.id,
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={Math.ceil(totalCount / PAGE_SIZE)}
        onPageChange={setPage}
      />

      {confirmDialog}
      <Toast toast={toast} />
    </div>
  );
}
